import { Account, LifestyleProtectionPolicy } from '@ndvr/portfolio-api';

import { AllocationRunOutput, SecuredAllocationType } from '@/cip/_openapi/portfolio-optimizer';

/** Lifestyle protection as the policy page edits it. `level` is the initial securedness in whole percent. */
export interface LifestyleDraft {
    enabled: boolean;
    accountGUID?: string;
    level: number;
    targetEndDate?: string;
}

export const MIN_LEVEL = 5;
export const MAX_LEVEL = 100;
export const LEVEL_STEP = 5;
/** What the portfolio page's Lifestyle Protection card starts a newly enabled protection at. */
export const DEFAULT_LEVEL = 5;

export const clampLevel = (level: number) => Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, Math.round(level)));

/** Protection is pinned to one account: the first with a non-zero initial securedness, as on the portfolio page. */
export const lifestyleFromAccounts = (accounts: Account[], targetEndDate?: string): LifestyleDraft => {
    const secured = accounts.find((account) => Number(account.initialSecuredness) > 0);
    return secured
        ? {
              enabled: true,
              accountGUID: secured.accountGUID,
              level: clampLevel(Number(secured.initialSecuredness) * 100),
              targetEndDate,
          }
        : { enabled: false, level: DEFAULT_LEVEL, targetEndDate };
};

export const sameLifestyle = (a: LifestyleDraft, b: LifestyleDraft) =>
    a.enabled === b.enabled && (!a.enabled || (a.accountGUID === b.accountGUID && a.level === b.level));

export const toLifestylePolicy = (draft: LifestyleDraft): LifestyleProtectionPolicy =>
    draft.enabled
        ? {
              enabled: true,
              accountGUID: draft.accountGUID,
              initialSecuredness: String(draft.level / 100),
              targetEndDate: draft.targetEndDate,
          }
        : { enabled: false };

/** The secured amount and level the optimizer targets for the accounts in scope. */
export interface PolicySecuredTargets {
    amount?: number;
    percentage?: number | 'mixed';
    /** The optimizer ran but returned nothing for at least one account in scope, so no target exists to show. */
    notCalculated?: boolean;
}

export const summarizePolicySecuredTargets = (
    allocation: AllocationRunOutput | undefined,
    accountGUIDs: string[],
): PolicySecuredTargets => {
    if (!allocation || accountGUIDs.length === 0) return {};
    const scope = new Set(accountGUIDs);
    const securedness = [...scope].map((guid) => allocation.securedness.find((item) => item.accountGuid === guid));
    // Missing accounts are not evidence of zero coverage.
    if (securedness.some((item) => !item)) return { notCalculated: true };
    // The optimizer returns null targets for accounts without Lifestyle protection.
    // Those inactive accounts must not hide another account's valid policy target.
    const percentages = securedness
        .filter((item) => item?.initialSecuredness !== 0 || item?.targetSecuredness != null)
        .map((item) => item?.targetSecuredness);
    const percentagesKnown =
        percentages.length > 0 &&
        percentages.every((value) => value != null && Number.isFinite(value) && value >= 0 && value <= 1);
    const amounts = allocation.targetAllocations
        .filter((item) => scope.has(item.accountGuid) && item.allocationType === SecuredAllocationType.PAYDAY_BOND)
        .map((item) => item.targetExposure);
    return {
        amount: amounts.every((value) => Number.isFinite(value) && value >= 0)
            ? amounts.reduce((sum, value) => sum + value, 0)
            : undefined,
        // Securedness is not an AUM percentage. The response does not provide the
        // withdrawal-liability denominator needed for a weighted policy rollup.
        percentage: percentagesKnown
            ? percentages.every((value) => value === percentages[0])
                ? percentages[0]
                : 'mixed'
            : undefined,
    };
};
